import { AfterViewInit, Component, OnDestroy, inject } from '@angular/core';
import SwaggerUI from 'swagger-ui';
import { ApisService, I18nService, NotificationService, OauthService, ProjectService } from '@wms/services';
import React from "react";
import { HTTP_METHODS, WS_CONSTANTS } from '@wms/core';
import { cloneDeep, some, get, forEach, includes, findKey, forOwn, set } from 'lodash-es';
import { ActivatedRoute } from '@angular/router';
import { SWAGGER_CONSTANTS } from '@wms/app/variables';
import { WmsWorkspaceManagerService } from "@wms/workspace";
import { replace } from 'projects/core/src/utils/utils';
import { EventManagerService } from '@wms/app/event-manager';

const multipartDataForCurl = { } as {[key : string] : any}
interface Tag {
  get(key: string): string; 
}

const OperationSummaryPlugin = () => {
  return {
    wrapComponents: {
      OperationSummary:
        (Original: any, system: any) =>
          (props: any) => {
            const httpMethodsMap = new Map([
              ["list","get"],
              ["create","post"],
              ["update","put"],
              ["delete","delete"]
            ])
            const currentHttpMethod = props.operationProps.get('method')
            const spec = system.specSelectors.specJson();
            const serviceType = spec.get('info').get('x-WM-SERVICE_TYPE')
            const paths = spec.get('paths')
            const currentPath = props.operationProps.get('path')
            const operationType = paths.get(currentPath).get(currentHttpMethod).get('x-WM-OPERATION_TYPE')
            //Dropdown options for Open API Bundles
            const options = [
                React.createElement('option', {value : '', key : 'empty'}, ''),
                React.createElement('option', {value : 'get', key : 'get'}, 'LIST'),
                React.createElement('option', {value : 'post', key : 'post'}, 'CREATE'),
                React.createElement('option', {value : 'put', key : 'put'}, 'UPDATE'),
                React.createElement('option', {value : 'delete', key : 'delete'}, 'DELETE'),
            ]
            //Dropdown options for Java Service
            const JS_Options = [
                React.createElement('option', {value : 'get', key : 'get'}, 'GET'),
                React.createElement('option', {value : 'post', key : 'post'}, 'POST'),
                React.createElement('option', {value : 'put', key : 'put'}, 'PUT'),
                React.createElement('option', {value : 'delete', key : 'delete'}, 'DELETE'), 
                React.createElement('option', {value : 'head', key : 'head'}, 'HEAD'),
                React.createElement('option', {value : 'patch', key : 'patch'}, 'PATCH')
            ]
            
            return React.createElement(
              'div',
              null,
              React.createElement(Original, props),
              React.createElement(
                'select',
                {
                  onChange: ( $event: any) => {
                    document.dispatchEvent(
                      new CustomEvent('updateSwagger', {
                        detail: { targetEl: $($event.target).closest('.opblock') },
                      })
                    );
                  },
                  defaultValue: !props.operationProps.get('tag').includes('Other endpoints') ? (serviceType === 'JavaService' ? currentHttpMethod : httpMethodsMap.get(operationType) )  : '' 
                },
                ...(serviceType === 'JavaService'? JS_Options : serviceType === 'OpenAPIService' ? options : [])
              )
            );
          },
    },
  };
};
const InfoContainerPlugin = function () {
  return {
    wrapComponents: {
      InfoContainer:
        (Original: any) =>
          (props: any) => {
            return React.createElement('div');
          },
    },
  };
};
const SchemeContainerPlugin = function () {
  return {
    wrapComponents: {
      SchemesContainer:
        (Original: any) =>
          (props: any) => {
            return React.createElement('div');
          },

    },
  };
}

const AuthorizationPlugin = function () {
  return {
    wrapComponents: {
      AuthorizeBtnContainer:
        (Original: any, system: any) =>
          (props: any) => {
            let isBasicAuthSupported = false
            const spec = system.specSelectors.specJson();
            const securityDefinitions = spec.get('securityDefinitions')?.toJS()
            if(securityDefinitions){
              isBasicAuthSupported = Object.entries(securityDefinitions).some(([_, value] : [string, any])=> value.type === 'basic')
            }

            if(isBasicAuthSupported){
              return React.createElement(Original, props)
            }else{
              return React.createElement('div');
            }
      }
    }
  }
}

const shownDefinitionsSelectorPlugin = function(system: any) {
  return {
    statePlugins: {
      auth: {
        wrapSelectors: {
          shownDefinitions: (oriSelector: any, system: any) => (state: any, ...args: any) => {
            const definitions = oriSelector(state, ...args)
            let modifiedDefinitions;
            if(definitions){
              const definitionsInJS = definitions.toJS().filter((auth: any)=> {
                const [_, value]: [string, any] = Object.entries(auth)[0];
               return value.type === 'basic'
              })
              const basicSecurityDef = definitionsInJS[0]
              const [key, value]: [string, any] = Object.entries(basicSecurityDef)[0]
              basicSecurityDef[key] = system.Im.OrderedMap(value)
              const definitionsInImJS = [system.Im.Map(basicSecurityDef)]
              modifiedDefinitions = system.Im.List(definitionsInImJS)
            }
            return modifiedDefinitions
          }
        }
      }
    }
  }
}

const MarkDownPlugin = function () {
  return {
    wrapComponents: {
      Markdown:
        (Original: any, system: any) =>
          (props: any) => {
            const spec = system.specSelectors.specJson();
            const tags: any = spec.get('tags')
            const tag = Array.from(tags as Tag[])?.[0].get('description');
            if(tag === props.source){
              return React.createElement('div');
            }else{
              return React.createElement(Original, props)
            }
          },
    },
  };
}

const RequestSelectorPlugin = function(system: any) {
  return {
    statePlugins: {
      spec: {
        wrapSelectors: {
          mutatedRequestFor: (origSelector: any) => (state: any, path: string, method: string) => {
            let useProxy = false
            const isMultipartReq = system.specSelectors.consumesOptionsFor([path, method])?.includes('multipart/form-data')
            const origRequest = system.specSelectors.mutatedRequests(state).getIn([path, method], null)?.toJS()
            useProxy = origRequest?.url.includes('/restservices/invoke')
            const updatedReq = useProxy ? constructRequest() : origRequest 

            function constructRequest(){
              const multipartReq = get(multipartDataForCurl,[path, method]);
              const request = isMultipartReq ? multipartReq?.['requestConfig'] : JSON.parse(origRequest.body) 
              const newReq = {} as any;
              newReq.url = request?.endpointAddress
              newReq.method = request?.method
              const headers = new Map()
              forOwn(request?.headers, (value, key)=>{
                headers.set(key, value)
              })
              headers.set('Content-Type', request?.contentType)
              headers.set('accept', system.specSelectors.currentProducesFor([path, method]))
              newReq.headers = system.Im.OrderedMap(headers)
              newReq.authDetails = request?.authDetails
              newReq.body ={} as any
              if(isMultipartReq){
                const body = {} as any
                for(let [k, v] of multipartReq?.['formData'].entries()){
                  if(k !== SWAGGER_CONSTANTS.WM_HTTP_JSON )
                     body[k] = v
                  }
                  newReq.body = system.Im.OrderedMap(body)
              }else{
                newReq.body = request?.requestBody
              }
              return newReq
            }
            return system.Im.OrderedMap(updatedReq)
          }
        }
      }
    }
  }
}

@Component({
  standalone: true,
  selector: 'wms-swagger-ui',
  templateUrl: './swagger-ui.component.html',
  styleUrls: ['./swagger-ui.component.less']
})
export class SwaggerUiComponent implements AfterViewInit, OnDestroy {
  pathRegex = /\/([a-zA-Z0-9]{0,})/g;
  pathParams: any;
  pathParamsSubscription: any;
  apiDocsSubscription: any;
  tagInfo: any; 
  crudEndpointsCounter: any;
  otherEndpointsCounter: any;
  methodTypes: any = ['get','put','post','delete', 'patch', 'head', 'options'];
  httpMethodTyesMap = new Map([
    ["get", "list"],
    ["post","create"],
    ["put", "update"],
    ["delete", "delete"]
  ])
  boundHandleSwaggerUpdate: ($event: any) => void;
  private wsManager: WmsWorkspaceManagerService;
  notificationService: NotificationService 
  locale: any 
  serviceType: string

  constructor(private apiService: ApisService, private projectService: ProjectService, private oAuthService: OauthService, private route: ActivatedRoute, private i18nService: I18nService, private eventManagerService:EventManagerService) {
    this.boundHandleSwaggerUpdate = this.handleSwaggerUpdate.bind(this);
    this.wsManager = inject(WmsWorkspaceManagerService);
    this.notificationService = inject(NotificationService);
    this.locale = this.i18nService.getMessages()
    this.eventManagerService.updateMockAPIResponseObservable().subscribe(()=>{
      this.projectService.setAppDeployedUrl('')
    })
  }

  ngAfterViewInit(): void {
    this.pathParamsSubscription = this.route.params.subscribe((params) => {
      this.pathParams = cloneDeep(params);
    });
    this.apiDocsSubscription = this.apiService._apiDocsSubject.subscribe((nv: any) => {
      if(this.apiService.config) nv.apiDocs.host = this.apiService.config.host
      this.apiService.filteredPaths_length = Object.keys(nv.apiDocs.paths).length;
      this.apiService.originalSwaggerSpec = cloneDeep(nv.apiDocs)
      this.renderSwaggerUI(nv);
    });
    document.addEventListener('updateSwagger', this.boundHandleSwaggerUpdate , false );

    $(document).on('mouseover.SwaggerEvents', '.opblock-summary-description', function () {
      $(this).attr('title', $(this).text());
    });

    $(document).on('click.SwaggerEvents', '.copy-to-clipboard', function () {
      $(this).css("background-color", "#555c71")
      setTimeout(() => {
        $(this).css("background-color", "")
      }, 200)
    })
  }

  handleSwaggerUpdate ($event: any)  {
    let selectElId;
    let selectEl;
    let changesMade = false;
    const modifiedEndpoint: string[]=[]
    const targetElId =  $event.detail.targetEl.attr('id');
    if (targetElId.includes('Other_endpoints')) {
      selectEl = $("h3[id*='CRUD_endpoints']").closest('.opblock-tag-section').find('select');
    } else {
      selectEl = $("h3[id*='Other_endpoints']").closest('.opblock-tag-section').find('select');
    }
    const targetVal = $($event.detail.targetEl).find('select').val() as string;
    const targetOperationId = this.extractOperationId($event.detail.targetEl.attr('id'))
    for(let i=0; i < selectEl.length; i++) {
      if (selectEl[i].value == targetVal) {
        selectEl[i].value = '';
        const selectElParentDiv: any = selectEl[i].closest('.opblock');
        selectElId = this.extractOperationId(selectElParentDiv.getAttribute('id'))
      }
    }

    for (let tag in this.apiService.tagInfoOriginal.paths) {
      for (let path in this.apiService.tagInfoOriginal.paths[tag]) {
        if (typeof this.apiService.tagInfoOriginal.paths[tag][path] === 'object') {
          if (this.apiService.tagInfoOriginal.paths[tag][path]['operationId'] === targetOperationId) {
            modifiedEndpoint.push('/'+tag.split('/').slice(2).join('/'))
            modifiedEndpoint.push(path.toUpperCase())
            if (this.apiService.activeServiceType !== 'JavaService') {
              const selectedOperation = this.httpMethodTyesMap.get(targetVal) || ''
              changesMade = get(this.apiService.originalSwaggerSpec,`paths.${tag}.${path}.x-WM-OPERATION_TYPE`) !== selectedOperation
              this.apiService.tagInfoOriginal.paths[tag][path]['x-WM-OPERATION_TYPE'] = selectedOperation
            } else {
              const originalMethod = findKey(this.apiService.originalSwaggerSpec.paths[tag],(pathObj)=>pathObj.operationId === targetOperationId)
              changesMade = originalMethod !== targetVal
              this.apiService.tagInfoOriginal.paths[tag][targetVal] = this.apiService.tagInfoOriginal.paths[tag][path];
              delete this.apiService.tagInfoOriginal.paths[tag][path];
            }
          } else if (this.apiService.tagInfoOriginal.paths[tag][path]['operationId'] === selectElId) {
            this.apiService.tagInfoOriginal.paths[tag][path]['x-WM-OPERATION_TYPE'] = '';
          }
        }
      }
    }
    this.apiService.initiateDirtyCheck()
    if(changesMade){
      $($event.detail.targetEl).on('click', "button.try-out__btn:contains('Try it out')", {modifiedEndpoint}, this.disableTryItOutBtn)
      const cancelbtn = $($event.detail.targetEl).find('button.try-out__btn.cancel')
      cancelbtn.length && cancelbtn.trigger('click')
    }else{
      $($event.detail.targetEl).off('click', "button.try-out__btn:contains('Try it out')", this.disableTryItOutBtn)
    }
  }

  extractOperationId(elementId: string){
    /* 
     elementId format -> "operations-(tag name)-operationId",
     Any space would be replaced by underscore, example -> "operations-pet_-_CRUD_endpoints-swagger_addPet"
        here, tag name -> "pet - CRUD endpoints"
        operationId -> "swagger_addPet"
     */
    const tagNames = this.tagInfo.tags.map((tag: any)=> tag.name.replace(/ /g, "_"))
    const prefixes = tagNames.join('-|') + '-'
    const pattern = `(?:${prefixes})(.*)`;
    const match = elementId.match(pattern)
    return match ? match[1] : null;
  }

  disableTryItOutBtn = (event : any) => {
    event.stopImmediatePropagation(); // Stop swagger's click handler from being executed
    this.notificationService.show('info', '', replace(this.locale.MESSAGE_SAVE_API_CHANGES , event.data.modifiedEndpoint))
  } 
  ngOnDestroy() {
    if (this.pathParamsSubscription) {
      this.pathParamsSubscription.unsubscribe();
    }
    if (this.apiDocsSubscription) {
      this.apiDocsSubscription.unsubscribe();
    }
    $(document).off('.SwaggerEvents');
    document.removeEventListener('updateSwagger', this.boundHandleSwaggerUpdate, false )
  }

  renderSwaggerUI(nv: any) {
    this.apiService.tagInfoOriginal = nv.apiDocs;
    this.swaggerUI(nv);
  }


  swaggerUI(nv: any) {
    const swaggerEl = document.querySelectorAll('[id=swagger-ui]');
    let plugins: any = [InfoContainerPlugin, SchemeContainerPlugin, AuthorizationPlugin, MarkDownPlugin, RequestSelectorPlugin, shownDefinitionsSelectorPlugin];
    this.serviceType = this.apiService.tagInfoOriginal.info['x-WM-SERVICE_TYPE']
    if (this.serviceType === 'OpenAPIService' || this.serviceType === 'JavaService') {
      plugins.push(OperationSummaryPlugin);
    }
    SwaggerUI({
      domNode: swaggerEl[swaggerEl.length - 1] as any,
      spec: this.getCategorizedTagInfo(nv.apiDocs),
      defaultModelsExpandDepth: -1,
      requestInterceptor: async (req: any) => {
        let data: any = {};
        if (this.projectService.getDeployedUrl()) {
          data = this.handleSwaggerRequest(req)
        } else {
          await this.projectService.getAppRunURL().then(() => { 
            data = this.handleSwaggerRequest(req)
          }).catch((err: any) => {
            console.log(err)
          })
        }
        return data
      },
      responseInterceptor: (res: any) => {
        if (this.apiService.config && !this.apiService.config.useProxy) {
          return res;
        }
        res.status = res.body.statusCode || res.obj.statusCode
        res.text = res.body.responseBody;
        return res;
      },
      plugins: plugins,
    });
  }

  //Append httpDetails to existing formData
  getFullFormData(requestData: any, httpDetails: any) {
    requestData.append(SWAGGER_CONSTANTS.WM_HTTP_JSON, new Blob([JSON.stringify(httpDetails)], {
      type: 'application/json'
    }));
    return requestData;
  }
  handleSwaggerRequest(req: any) {
    const url = new URL(req.url);
    /* 
      example URL: https://www.example.com/abc/def/path1/path2,
      host : www.example.com/abc/def
      pathname : /path1/path2
    -If the host name contains '/', the above URL Object will only consider 'www.example.com' as a host name and 
    the remaining (/abc/def/path1/path2) will be considered as a the path name. So removing part of host name from pathname*/
    if(this.tagInfo.host && this.tagInfo.host.includes('/')){
      const index = this.tagInfo.host.indexOf('/')
      const partialHostName = this.tagInfo.host.slice(index)
      url.pathname = url.pathname.replace(partialHostName,'')
    }
    const paths = Object.keys(this.tagInfo.paths);
    if (this.apiService.config && !this.apiService.config.useProxy && this.serviceType === 'OpenAPIService') {
      paths.forEach((path: any) => {
        let resolvedPath: any;
        if (path.includes('{')) {
          resolvedPath = this.resolvePathParams(url.pathname.replace('/services', ''), path);
        }
        if ((this.tagInfo.basePath === '/' && url.pathname === path) || (this.tagInfo.basePath !== '/' && url.pathname.replace(this.tagInfo.basePath, '') === path) || resolvedPath) {
          let completeUrl = this.tagInfo.paths[path]['x-WM-COMPLETE_PATH'];
          if (resolvedPath) {
            completeUrl = completeUrl.replace(path, resolvedPath);
          }
          let completeUrlParams = completeUrl.split(':');
          completeUrl = url.protocol + completeUrlParams[1];
          const hostPlaceholder = completeUrl.slice(completeUrl.indexOf('$'), completeUrl.indexOf('}') + 1);
          completeUrl = completeUrl.replace(hostPlaceholder, this.apiService.config.host);
          const basePathPlaceholder = completeUrl.slice(completeUrl.indexOf('$'), completeUrl.indexOf('}') + 1);
          if (this.apiService.config.basePath && this.apiService.config.basePath !== '/') {
            completeUrl = completeUrl.replace(basePathPlaceholder, this.apiService.config.basePath);
          } else {
            completeUrl = completeUrl.replace(basePathPlaceholder, '');
          }
          req.url = completeUrl + url.search;
        }
      });
      if (req.url.startsWith('http:')) {
        req.url = req.url.replace('http', 'https');
      }
      return req;
    }
    let pathName: string
    let methodName: any;
    let methodConsumes: any;
    paths.forEach((path: any)=>{
      const resolvedPath: any = this.resolvePathParams(url.pathname, path);
      let swaggerDefinedPath: any =  this.tagInfo.paths[path]['x-WM-BASE_PATH'] + this.tagInfo.paths[path]['x-WM-RELATIVE_PATH'];
      swaggerDefinedPath = swaggerDefinedPath.replace(`/${this.apiService.activeApi?.serviceId}`, '');
      if(swaggerDefinedPath === url.pathname || resolvedPath){
        const relativePath: any = resolvedPath && path.includes('{')? resolvedPath : this.tagInfo.paths[path]['x-WM-RELATIVE_PATH'];
        const relativePathArgs: any = this.tagInfo.paths[path]['x-WM-RELATIVE_PATH'].split('/');
        methodName = relativePath ? relativePathArgs[relativePathArgs.length - 1] : undefined;
        if (path.includes('{')) {
          url.pathname = this.apiService.activeApi?.serviceId + relativePath;
        } else {
          url.pathname = this.tagInfo.paths[path]['x-WM-BASE_PATH'] + (relativePath.replace(`/${this.apiService.activeApi?.serviceId}`, ''));
        }
        methodConsumes = this.tagInfo.paths[path][req.method.toLowerCase()].consumes;
        pathName = path
      }
    });
    if (!methodName) {
      const methodNameEl = url.pathname.split('/');
      methodName = methodNameEl[methodNameEl.length - 1];
    }
    const reqObj = cloneDeep(req);
    const isBasicAuth = this.isBasicAuthConfigured(req)
    const constructProxyRequest = (res?: any) => {
      let isMultiPart = includes(methodConsumes, WS_CONSTANTS.CONTENT_TYPES.MULTIPART_FORMDATA);
      req.method = 'POST';
      req.url = window.location.origin + '/studio/services/projects/' + this.projectService.getProjectId() + '/restservices/invoke';
      req.requestBody = req.body;
      req.headers = {'Content-Type': (isMultiPart && methodConsumes && methodConsumes[0]) || 'application/json'}
      reqObj.headers['skipSecurity'] = 'true'
      req.body = {
        "contentType": (methodConsumes && methodConsumes[0]) || "application/json",
        "endpointAddress": 'https:' + this.projectService.getAppDeployedUrl() + '/services' + url.pathname + url.search,
        "headers": reqObj.headers,
        "method": reqObj.method,
      };
      if (reqObj.body && !isMultiPart) {
        req.body['requestBody'] = reqObj.body;
      }
      if (res && Object.keys(res).length) {
        const reqBody = req.body;
        reqBody.headers['Authorization'] = 'Bearer ' + res.token;
        reqBody['authDetails'] = {
          "type": "OAUTH2",
          "providerId": res.providerId
        };
      }
      //For form-data method append httpDetails to the requestData
      if (isMultiPart) {
        set(multipartDataForCurl, [pathName, reqObj.method.toLowerCase(), 'requestConfig'], req.body)
        set(multipartDataForCurl, [pathName, reqObj.method.toLowerCase(), 'formData'], reqObj.body)
        req.body = this.getFullFormData(reqObj.body, req.body);
      } else {
        req.body = JSON.stringify(req.body);
      }
      return req;
    }
      if(isBasicAuth){
        return constructProxyRequest();
      }else{
        return this.testApi(methodName, req).then(constructProxyRequest);
      }
  }
  resolvePathParams(url: any, path: any) {
    const pathArgs = path.split('/');
    const urlArgs = url.split('/');
    forEach(pathArgs, (item, index) => {
      if (item.startsWith('{')) {
        pathArgs[index] = urlArgs[index];
      }
    });
    if (url === pathArgs.join('/')) {
      return pathArgs.join('/');
    }
  }

  isBasicAuthConfigured(request: any){
    const authHeader = request.headers['Authorization'] || request.headers['authorization']; 
    return !!authHeader && authHeader.startsWith('Basic ') 
  }

  testApi(methodName: any, req: any): any {
    let selectedPath, providerId: any;
    for (let path in this.tagInfo.paths) {
      const pathArgs = path.split('/');
      if (methodName === pathArgs[pathArgs.length - 1]) {
        selectedPath = this.tagInfo.paths[path][req.method.toLocaleLowerCase()];
        let oAuthRequired = false, securityDef;
        if (selectedPath.security && selectedPath.security.length) {
          some(selectedPath.security, (methodInfo): any => {
            securityDef = get(this.tagInfo.securityDefinitions, Object.keys(methodInfo)[0]);
            if(securityDef && securityDef['x-WM-PROVIDER_ID'] == this.apiService.activeProvider?.providerId && securityDef.type === 'oauth2') {
              providerId = securityDef['x-WM-PROVIDER_ID'];
              oAuthRequired = true;
              return true;
            }
          });
        }

        if (oAuthRequired) {
          let accessToken = sessionStorage.getItem(this.oAuthService.getAccessTokenPlaceholder(providerId));
          // && token !== 'error' -- TO DO
          if (!accessToken) {
            let authURL = get(this.apiService.activeProvider, 'oauth2Flow');
            authURL = authURL && (authURL.toLowerCase() === 'implicit' || get(this.apiService.activeProvider, 'oAuth2Pkce.enabled') === true) ? authURL : this.apiService.authorizationUrl;
            return this.oAuthService.performAuthorization(authURL, this.apiService.activeProvider.providerId, this.apiService.activeProvider).then((res) => {
              return Promise.resolve({
                'token': res,
                'providerId': this.apiService.activeProvider.providerId
              });
            });
          } else {
            return Promise.resolve({
              'token': accessToken,
              'providerId': this.apiService.activeProvider.providerId
            });
          }
        } else {
          return Promise.resolve({});
        }
      }
    }
  }

  getCategorizedTagInfo(endpointInfo: any): any {
    this.crudEndpointsCounter = 0;
    this.otherEndpointsCounter = 0;
    let endpoint = cloneDeep(endpointInfo);
    const paths = Object.keys(endpoint.paths);
    let pathInfo: any;
    if (!Object.keys(endpoint.paths).length || endpoint.tags[0]?.name.includes('CRUD endpoints') || endpoint.info['x-WM-SERVICE_TYPE'] !== 'OpenAPIService') {
      paths.forEach((path) => {
        pathInfo = endpoint.paths[path];
        pathInfo.spl
        const pathKeys = Object.keys(pathInfo);
        pathKeys.forEach((key) => {
          if ((this.methodTypes.includes(key)) && !pathInfo[key].summary) {
            pathInfo[key].summary = pathInfo[key].description;
          }
        });
      });
      if(pathInfo && Object.keys(pathInfo).includes('x-WM-RELATIVE_PATH')){
        this.updatePathName(paths,endpoint);
      }
      this.tagInfo = endpoint;
      return endpoint;
    }
    // creating 2 tags under each endpoints - CRUD and Other
    endpoint.tags.push(cloneDeep(endpoint.tags[0]));
    const tagName = endpoint.tags[0].name;
    endpoint.tags[0].name = tagName + " - CRUD endpoints";
    endpoint.tags[1].name = tagName + " - Other endpoints";

    // Modify the tag name of each path based on x-WM-OPERATION_TYPE key
    paths.forEach((path) => {
      pathInfo = endpoint.paths[path];
      pathInfo.spl
      const pathKeys = Object.keys(pathInfo);
      pathKeys.forEach((key) => {
        if((this.methodTypes.includes(key)) && !pathInfo[key].summary) {
          pathInfo[key].summary = pathInfo[key].description;
        }
        if (HTTP_METHODS.includes(key.toUpperCase())) {
          if (pathInfo[key]['x-WM-OPERATION_TYPE']) {
            pathInfo[key].tags[0] = endpoint.tags[0]?.name;
            this.crudEndpointsCounter+= 1;
          } else {
            pathInfo[key].tags[0] = endpoint.tags[1]?.name;
            this.otherEndpointsCounter+=1;
          }
        }
      });
      let pathRegArr = path.match(this.pathRegex);
      if (pathRegArr) {
        let modPathRegArr =  pathRegArr.splice(0, 1);
        path = modPathRegArr.join();
      }

    });
    if(this.crudEndpointsCounter === 0){
      endpoint.tags.splice(0,1);
    }
    if(this.otherEndpointsCounter === 0){
      endpoint.tags.splice(1,1);
    }
    if (!endpoint.basePath && this.apiService.config && !this.apiService.config.useProxy) {
      endpoint.basePath = this.apiService.config.basePath;
    }
    if(pathInfo && Object.keys(pathInfo).includes('x-WM-RELATIVE_PATH')){
      this.updatePathName(paths,endpoint);
    }
    this.tagInfo = endpoint;
    return endpoint;
  }

  // remove serviceId from pathname and show base path
  updatePathName(paths: any, endpoint: any){
    if (this.apiService.importDialogOpened) {
      return;
    }
    paths.forEach((path: any)=>{
      const hasServiceId: boolean =  path.includes(`/${this.apiService.activeApi?.serviceId}`);
      if (hasServiceId) {
        let modifiedPath: any = path.replace(`/${this.apiService.activeApi?.serviceId}`, '');
        endpoint.paths[modifiedPath] = endpoint.paths[path];
        delete endpoint.paths[path];
      }
    });
  }
}
